// Set concept-skill catalogue. ADR 0018 §3 / docs/04 §4.4.
//
// 22 concept skills — one per equipment set (ADR 0018 §1). These are a
// catalogue distinct from the ADR 0015 item-skill catalogue (`item.*`): a set
// grants its concept skill once the first `SetBonus` tier activates.
//
// ID schema (ADR 0018 §3 / §J):
//   set-skill.<setId>.<keyword>     e.g. `set-skill.set.aegis-vow.covenant-fill`
//   The `set-skill.<setId>` prefix must match the owning `SetDefinition.id`.
//
// Scope note: per ADR 0015 Codex SUGGESTION B these concept skills are NOT
// upper-compatible clones of the 405 item skills — Phase 4 wires set-specific
// gating (stage-linked scaling / same-set counters) that the current
// `Activation` type cannot yet express. The `effects` / `activation` here are
// provisional thematic placeholders; the sim reducer and numeric calibration
// are out of ADR 0018 scope (§未確定事項 / Phase 4).
import { seconds } from '@renavora/shared';
// =============================================================================
// カテゴリ A: T3/T4 ジョブ体現セット (9)
// =============================================================================
const CATEGORY_A = {
    'set-skill.set.aegis-vow.covenant-fill': {
        id: 'set-skill.set.aegis-vow.covenant-fill',
        name: 'Aegis Vow: Covenant Fill',
        source: 'item',
        kind: 'reactive',
        targeting: { shape: 'allyLowestHp' },
        activation: { onEvent: 'onTakenDamage' },
        effects: [{ kind: 'shield', power: 0.3, durationTicks: seconds(8) }],
    },
    'set-skill.set.seven-phase-crown.phase-chant': {
        id: 'set-skill.set.seven-phase-crown.phase-chant',
        name: 'Seven-Phase Crown: Phase Chant',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(8),
        range: 20,
        targeting: { shape: 'singleTarget' },
        activation: {},
        effects: [{ kind: 'damage', element: 'fire', power: 2.0 }],
    },
    'set-skill.set.overflowing-grace.surplus-grace': {
        id: 'set-skill.set.overflowing-grace.surplus-grace',
        name: 'Overflowing Grace: Surplus Grace',
        source: 'item',
        kind: 'passive',
        targeting: { shape: 'self' },
        activation: {},
        effects: [{ kind: 'shield', power: 0.2, durationTicks: seconds(600) }],
    },
    'set-skill.set.afterimage.afterimage-mark': {
        id: 'set-skill.set.afterimage.afterimage-mark',
        name: 'Afterimage: Afterimage Mark',
        source: 'item',
        kind: 'reactive',
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onAttacked' },
        effects: [{ kind: 'damage', element: 'physical', power: 0.5 }],
    },
    'set-skill.set.feast-of-the-dead.bone-legion': {
        id: 'set-skill.set.feast-of-the-dead.bone-legion',
        name: 'Feast of the Dead: Bone Legion',
        source: 'item',
        kind: 'trigger',
        // 敵死亡ごとの自動召喚に internal cooldown を課し、召喚レートを抑える
        // (ADR 0018 §6 設計制約)。
        cooldownTicks: seconds(3),
        targeting: { shape: 'self' },
        activation: { onEvent: 'onKill' },
        effects: [
            {
                kind: 'summon',
                spec: {
                    unitId: 'skeleton',
                    durationTicks: seconds(20),
                    count: 1,
                    // 陣営召喚上限 (20) 超過防止。owner 別 cap で全体上限を侵さない (ADR 0018 §6)。
                    maxConcurrentPerOwner: 4,
                },
            },
        ],
    },
    'set-skill.set.dragon-scale.dragonization': {
        id: 'set-skill.set.dragon-scale.dragonization',
        name: 'Dragon Scale: Dragonization',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(20),
        range: 8,
        targeting: { shape: 'circle', radius: 8, maxTargets: 8 },
        activation: {},
        effects: [{ kind: 'damage', element: 'fire', power: 2.5 }],
    },
    'set-skill.set.rune-empower.rune-enchant': {
        id: 'set-skill.set.rune-empower.rune-enchant',
        name: 'Rune Empower: Rune Enchant',
        source: 'item',
        kind: 'passive',
        targeting: { shape: 'allAllies' },
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'mighty' }],
    },
    'set-skill.set.magus-union.dual-reflection': {
        id: 'set-skill.set.magus-union.dual-reflection',
        name: 'Magus Union: Dual Reflection',
        source: 'item',
        kind: 'trigger',
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onHit' },
        effects: [{ kind: 'damage', element: 'weapon-derived', power: 0.8 }],
    },
    'set-skill.set.inviolable-sanctuary.sanctuary-field': {
        id: 'set-skill.set.inviolable-sanctuary.sanctuary-field',
        name: 'Inviolable Sanctuary: Sanctuary Field',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(30),
        targeting: { shape: 'allAllies' },
        activation: {},
        // 致死無効は新 sim reducer 前提 (ADR 0018 Codex SUGGESTION F3 / Phase 4)。
        // 暫定的に allAllies シールドで表現。
        effects: [{ kind: 'shield', power: 0.5, durationTicks: seconds(10) }],
    },
};
// =============================================================================
// カテゴリ B: シナジー軸特化セット (4)
// =============================================================================
const CATEGORY_B = {
    'set-skill.set.mark-accumulation.mark-acceleration': {
        id: 'set-skill.set.mark-accumulation.mark-acceleration',
        name: 'Mark Accumulation: Mark Acceleration',
        source: 'item',
        kind: 'passive',
        targeting: { shape: 'self' },
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'charge' }],
    },
    'set-skill.set.plague-spread.contagion': {
        id: 'set-skill.set.plague-spread.contagion',
        name: 'Plague Spread: Contagion',
        source: 'item',
        kind: 'trigger',
        targeting: { shape: 'circle', radius: 5, maxTargets: 5 },
        activation: { onEvent: 'onStatusApply' },
        effects: [{ kind: 'applyDebuff', debuffId: 'poison' }],
    },
    'set-skill.set.fortress-architect.expansion': {
        id: 'set-skill.set.fortress-architect.expansion',
        name: 'Fortress Architect: Expansion',
        source: 'item',
        kind: 'passive',
        targeting: { shape: 'self' },
        activation: {},
        // 設置物の同時設置数 +1 は template 別 owner 上限のみ増やし、陣営設置物上限
        // (24) は不変 (ADR 0018 §設計制約)。passive Buff は暫定表現。
        effects: [{ kind: 'applyBuff', buffId: 'earth_guard' }],
    },
    'set-skill.set.killing-chain.chain-sustain': {
        id: 'set-skill.set.killing-chain.chain-sustain',
        name: 'Killing Chain: Chain Sustain',
        source: 'item',
        kind: 'passive',
        targeting: { shape: 'self' },
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'kill_streak' }],
    },
};
// =============================================================================
// カテゴリ C: 属性特化セット (3)
// =============================================================================
const CATEGORY_C = {
    'set-skill.set.conflagration.flame-spread': {
        id: 'set-skill.set.conflagration.flame-spread',
        name: 'Conflagration: Flame Spread',
        source: 'item',
        kind: 'trigger',
        targeting: { shape: 'circle', radius: 5, maxTargets: 5 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'damage', element: 'fire', power: 1.0 }],
    },
    'set-skill.set.absolute-zero.frost-zone': {
        id: 'set-skill.set.absolute-zero.frost-zone',
        name: 'Absolute Zero: Frost Zone',
        source: 'item',
        kind: 'trigger',
        targeting: { shape: 'circle', radius: 5, maxTargets: 5 },
        activation: { onEvent: 'onStatusApply' },
        effects: [{ kind: 'applyDebuff', debuffId: 'chill' }],
    },
    'set-skill.set.abyss.erosion': {
        id: 'set-skill.set.abyss.erosion',
        name: 'Abyss: Erosion',
        source: 'item',
        kind: 'trigger',
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onHit' },
        effects: [{ kind: 'applyDebuff', debuffId: 'erosion' }],
    },
};
// =============================================================================
// カテゴリ D: 戦術コンセプトセット (3)
// =============================================================================
const CATEGORY_D = {
    'set-skill.set.last-stand.backwater': {
        id: 'set-skill.set.last-stand.backwater',
        name: 'Last Stand: Backwater',
        source: 'item',
        kind: 'passive',
        targeting: { shape: 'self' },
        activation: { selfHpAtMost: 0.25 },
        effects: [{ kind: 'applyBuff', buffId: 'fighting_spirit' }],
    },
    'set-skill.set.bulwark.bastion': {
        id: 'set-skill.set.bulwark.bastion',
        name: 'Bulwark: Bastion',
        source: 'item',
        kind: 'passive',
        targeting: { shape: 'self' },
        activation: {},
        effects: [{ kind: 'shield', power: 0.1, durationTicks: seconds(600) }],
    },
    'set-skill.set.attrition.endurance': {
        id: 'set-skill.set.attrition.endurance',
        name: 'Attrition: Endurance',
        source: 'item',
        kind: 'passive',
        periodTicks: seconds(5),
        targeting: { shape: 'self' },
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'mighty' }],
    },
};
// =============================================================================
// カテゴリ E: ギミック / アクセ / Luck セット (3)
// =============================================================================
const CATEGORY_E = {
    'set-skill.set.tri-relic.relic-release': {
        id: 'set-skill.set.tri-relic.relic-release',
        name: 'Tri-Relic: Relic Release',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(15),
        targeting: { shape: 'self' },
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'mighty' }],
    },
    'set-skill.set.reactive-engine.threshold-link': {
        id: 'set-skill.set.reactive-engine.threshold-link',
        name: 'Reactive Engine: Threshold Link',
        source: 'item',
        kind: 'reactive',
        targeting: { shape: 'self' },
        activation: { onEvent: 'onHpBelow', selfHpAtMost: 0.5 },
        effects: [{ kind: 'applyBuff', buffId: 'charge' }],
    },
    'set-skill.set.fortune-seeker.fortune': {
        id: 'set-skill.set.fortune-seeker.fortune',
        name: 'Fortune Seeker: Fortune',
        source: 'item',
        // sim 戦闘外のメタ効果 (conceptSkillKind: 'meta')。sim は本スキルを無視し、
        // drop ロジック (別 ADR / Phase 4) がドロップ率・品質補正に参照する。
        // Skill.kind は 4 値制約により 'passive' で表現。
        kind: 'passive',
        targeting: { shape: 'self' },
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'blessing' }],
    },
};
// =============================================================================
// SET_SKILLS export (catalogue 完成 = 22 件)
// =============================================================================
export const SET_SKILLS = {
    ...CATEGORY_A,
    ...CATEGORY_B,
    ...CATEGORY_C,
    ...CATEGORY_D,
    ...CATEGORY_E,
};
export const SET_SKILL_IDS = Object.keys(SET_SKILLS);
//# sourceMappingURL=set-skills.js.map